import * as React from "react";
import { Form, useFormApi } from "informed";
import * as moment from "moment";
import DocumentTitle from "../misc/components/DocumentTitle";
import { ExtractDefaultAlarms, SubjectFormModel } from "./SubjectsShared";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { Link } from "react-router-dom";
import { SubjectFormBody } from "./Forms/SubjectFormBody";
import { SubjectEdiaryFormBody } from "./Forms/SubjectEdiaryFormBody";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { DeviceTypes, SubjectTypes, Language } from "../misc/api/apiTypes";
import { ActionButtons } from "../misc/ActionButtons";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { Site, SubjectCreation, WebPortalClient } from "WebPortalClient";
import { Authorization } from "../misc/components/Authorization";
import { useSecurityContext } from "../Core/SecurityContext";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { ConfigurableDataItemIds } from "../misc/api/ApiConfigurableDataItems";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

export const CreateSubjectForm = () => {
    const { study, dataItemConfigurationCollection, diaryConfigFiles } = useCurrentStudyConfigurationContext();

    const formApi = useFormApi<SubjectFormModel>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [subjectLimitedLanguages, setSubjectLimitedLanguages] = React.useState<Language[]>([]);
    const [sites, setSites] = React.useState<Site[]>([]);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const loadInitial = async () => {
            const sitesResult = await client.client.getSites(undefined, `studyId eq ${study.id}`);
            setSites(sitesResult.value);
        }

        loadInitial();
    }, [study.id]);

    const onSubmit = async () => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        let state = formApi.getState();

        if (state.values.BirthDate && (state.values.Age === null || state.values.Age === undefined)) {
            var age = await legacyClient.getCalculateAge(state.values.BirthDate);
            formApi.setValue('Age', age);
            formApi.validate()
            state = formApi.getState();
        }

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }


        if (state.values.Pin !== state.values.PinConfirmation) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("PIN does not match the PIN Confirmation");
        }

        const sitesResult = await legacyClient.getSites({ Filters: [] });
        const site = sitesResult.Data.find(s => s.Id === state.values.SiteId);
        const subjectIdConfiguration = dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId);

        const apiForm = {
            ...state.values,
            StudyId: study.id,
            SubjectNumber: subjectIdConfiguration.prefixWithSiteNumber ? `${site.SiteNumber}${subjectIdConfiguration.prefixDelimiter}${state.values.SubjectNumber}` : state.values.SubjectNumber,
            SubjectTypeId: state.values.SubjectTypeId ? state.values.SubjectTypeId : SubjectTypes.InStudy,
            BedTime: state.values.BedTime ? state.values.BedTime : undefined,
            EdiarySubject: state.values.Pin ? {
                Pin: state.values.Pin,
                Alarms: state.values.Alarms ?? [],
            } : undefined
        };

        try {
            const subject = await client.client.postSubjects(
                {
                    id: apiForm.Id,
                    siteId: apiForm.SiteId,
                    populationGroupId: apiForm.PopulationGroupId,
                    number: apiForm.SubjectNumber,
                    language: apiForm.LanguageId,
                    gender: apiForm.GenderId,
                    dateOfBirth: apiForm.BirthDate,
                    age: apiForm.Age ?? null,
                    height: apiForm.Height,
                    weight: apiForm.Weight,
                    status: apiForm.SubjectTypeId,
                    bmi: apiForm.BMI,
                    smokerType: apiForm.SmokerTypeId,
                    bedTime: moment.duration(apiForm.BedTime).toISOString(),
                    pin: apiForm.EdiarySubject ? apiForm.EdiarySubject.Pin : null,
                    alarms: apiForm.EdiarySubject ? apiForm.EdiarySubject.Alarms.map(a => ({ time: moment.duration(a).toISOString() })) : [],
                    race: apiForm.Race
                } as SubjectCreation);

            if (subject) {
                setSuccessAlert(true);

                //E-Diary only checks
                if (study.sessionTypeIds.find(x => x === SessionTypes.EDiary)) {
                    const files = diaryConfigFiles;
                    if (files !== null && files !== undefined && subject.language !== null) {
                        const languageLocaleName = subject.language;

                        const stringFile = files.find(x => x.name.includes(languageLocaleName));
                        if (stringFile === null || stringFile === undefined) {
                            setWarningAlert(`The Diary will be configured with the default language as there is no matching translation for the selected language ‘${subject.languageName}’`);
                        }
                    }
                }
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/Subjects`}>Subjects list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Subject</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsHeader" returnToListRoute="/subjects" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="demographicsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Demographics</h4>
                        </div>
                        <div className="panel-body">
                            <SubjectFormBody sites={sites} isEdit={false} canEdit={true} onSetLimitedLanguages={setSubjectLimitedLanguages} subjectLimitedLanguages={subjectLimitedLanguages} />
                        </div>
                    </div>
                    {study.deviceTypeIds.find(dt => dt === DeviceTypes.In2itiveEDiary || dt === DeviceTypes.In2itiveP230EDiary) !== undefined ?
                        <div id="eDiarySetupPanel" className="panel panel-primary">
                            <div className="panel-heading">
                                <h4>eDiary Setup</h4>
                            </div>
                            <div className="panel-body">
                                <SubjectEdiaryFormBody sites={sites} enabled={true} onSetLimitedLanguages={setSubjectLimitedLanguages} subjectLimitedLanguages={subjectLimitedLanguages} />
                            </div>
                        </div> : null}
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsFooter" returnToListRoute="/subjects" />
                </div>
            </div>
        </>
    );
};

export const CreateSubjectScreen = () => {
    const { study, diarySettings } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    if (!diarySettings)
        return <LoadingWithMessage message="Loading configurations..." />

    const alarmString = diarySettings.DefaultEdiaryAlarm;

    const alarmTimes = alarmString ? ExtractDefaultAlarms(alarmString) : null;

    const formSubject = {
        Alarms: diarySettings.DefaultEdiaryAlarm ? alarmTimes : null
    }

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateSubject, PermissionEnum.CreateSiteSubjects]}>
            <DocumentTitle title="Create Subject | Vitalograph Web Portal">
                <Form noValidate initialValues={formSubject}>
                    <CreateSubjectForm />
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
